import { FC } from 'react';
import { Icon, Text, Heading } from '@livechat/design-system-react-components';
import { CodeBlack as CodeBlackIcon, TextLogo } from '@livechat/design-system-icons';

import { buildOauthQuery } from 'helpers/oath-query-builder';
import { organizationIDEscaped } from 'helpers/account';
import Amplitude from 'services/Amplitude';
import { pushExternalRedirectToDataLayer } from 'helpers/tracking';
import { useProducts } from 'hooks/use-products';
import { getLogoSource } from 'helpers/logo';
import { getAppLinkByName, getProductDisplayName, getProductName } from 'helpers/product';
import { useGetMe } from 'hooks/use-get-me';
import { FilteredOrganization } from 'types/organization';
import { PRODUCTS } from 'constants/product';
import { Product } from 'types/product';

import {
  productsContainerStyles,
  noProductsTextStyles,
  goToProductContainerStyles,
  linkStyles,
  goToProductStyles,
} from './styles';

type Props = {
  organization: FilteredOrganization;
  textPlatformAsProductEnabled: boolean;
};

export const OrganizationDetails: FC<Props> = ({ organization, textPlatformAsProductEnabled }) => {
  const { email } = useGetMe();
  const { getRedirectUrl, isFetchingProducts } = useProducts();

  const products = organization.products
    .filter(({ haveAccessToProduct }) => haveAccessToProduct)
    .map(({ name }) => name);
  const displayDeveloperConsole = products.includes('LiveChat') && !textPlatformAsProductEnabled;

  const devConsoleParam = {
    client_id: import.meta.env.VITE_APP_TP_CLIENT_ID,
    redirect_uri: import.meta.env.VITE_APP_TP_APP_URL,
    response_type: 'token',
  };

  const panelParam = {
    client_id: import.meta.env.VITE_APP_CLIENT_ID,
    redirect_uri: import.meta.env.VITE_APP_PANEL_URI,
    product: 'Accounts',
    response_type: 'token',
  };

  const filteredProducts = products.filter((product) => PRODUCTS.includes(product));

  const isTextAppOnlyOrg =
    filteredProducts.length === 1 && filteredProducts[0] === getProductName(Product.TextApp);

  const handleProductClick = (product: string) => {
    pushExternalRedirectToDataLayer(getAppLinkByName(product));
    Amplitude.logEvent(email, 'AVATAR_MENU_ITEM_CLICK', { menuElement: product }, true);
  };

  if (isFetchingProducts) {
    return null;
  }

  // Special case: Text App only organization
  if (isTextAppOnlyOrg) {
    return (
      <div css={productsContainerStyles} data-testid="organization-products">
        <div>
          <a
            css={linkStyles}
            href={import.meta.env.VITE_APP_TA_APP_URL}
            data-testid="product-link"
            onClick={() => handleProductClick('TextApp')}
          >
            <div css={goToProductContainerStyles}>
              <Icon source={TextLogo} size="large" />
              <Heading size="xs" css={goToProductStyles}>
                Go to Text App
              </Heading>
            </div>
          </a>
        </div>
      </div>
    );
  }

  // No products case
  if (filteredProducts.length === 0) {
    return (
      <div css={productsContainerStyles} data-testid="organization-products">
        <Text css={noProductsTextStyles}>You don&apos;t have products in this organization</Text>
      </div>
    );
  }

  // Default case: Show all products
  return (
    <div css={productsContainerStyles} data-testid="organization-products">
      <div>
        {filteredProducts.map((product) => (
          <a
            key={`${product}_${organization.id}`}
            href={getRedirectUrl(product, organization.id)}
            css={linkStyles}
            data-testid="product-link"
            onClick={() => handleProductClick(product)}
          >
            <div css={goToProductContainerStyles}>
              <Icon source={getLogoSource(product)} size="large" />
              <Heading size="xs" css={goToProductStyles}>
                Go to {getProductDisplayName(product)}
              </Heading>
            </div>
          </a>
        ))}
        <div>
          <a
            css={linkStyles}
            href={`/?organization_id=${organizationIDEscaped(organization.id)}&${buildOauthQuery(panelParam)}`}
            data-testid="product-link"
            onClick={() => handleProductClick('Accounts')}
          >
            <div css={goToProductContainerStyles}>
              <Icon source={TextLogo} size="large" />
              <Heading size="xs" css={goToProductStyles}>
                Go to Accounts
              </Heading>
            </div>
          </a>
        </div>
        <div>
          {displayDeveloperConsole && (
            <a
              css={linkStyles}
              href={`/?organization_id=${organizationIDEscaped(organization.id)}&${buildOauthQuery(
                devConsoleParam
              )}`}
              data-testid="product-link"
              onClick={() => handleProductClick('Developer console')}
            >
              <div css={goToProductContainerStyles}>
                <Icon source={CodeBlackIcon} size="large" />
                <Heading size="xs" css={goToProductStyles}>
                  Developer console
                </Heading>
              </div>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};
